import { apiUrl } from "config/api";
import { getAccessToken } from "utils/auth";
import { getSelectedLanguage, normalizeLanguageCode } from "utils/language";

const defaultHeaders = {
  Accept: "application/json",
};

const toJson = async (res) => {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return res.text();
};

const request = async (path, { method = "GET", headers, body, signal, credentials } = {}) => {
  const url = path.startsWith("http") ? path : apiUrl(path);
  const accessToken = getAccessToken();
  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await fetch(url, {
    method,
    headers: {
      ...defaultHeaders,
      ...authHeaders,
      ...headers,
    },
    body,
    signal,
    credentials,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with status ${response.status}`);
  }

  return toJson(response);
};

export const NEWS_PLACEHOLDER_URL = apiUrl("/api/news/placeholder");

export const getHotNews = ({ limit, offset, language, lang, ...options } = {}) => {
  const query = new URLSearchParams();
  if (typeof limit === "number") query.set("limit", String(limit));
  if (typeof offset === "number") query.set("offset", String(offset));
  const effectiveLang = normalizeLanguageCode(language || lang || getSelectedLanguage());
  if (effectiveLang) query.set("lang", effectiveLang);
  const qs = query.toString();
  return request(`/api/news/hot${qs ? `?${qs}` : ""}`, options);
};

export const getLatestNews = ({ userId, limit, offset, language, lang, ...options } = {}) => {
  const query = new URLSearchParams();
  if (typeof userId !== "undefined" && userId !== null && userId !== "") {
    query.set("user_id", String(userId));
  }
  if (typeof limit === "number") query.set("limit", String(limit));
  if (typeof offset === "number") query.set("offset", String(offset));
  const effectiveLang = normalizeLanguageCode(language || lang || getSelectedLanguage());
  if (effectiveLang) query.set("lang", effectiveLang);
  const qs = query.toString();
  const path = `/api/news/latest${qs ? `?${qs}` : ""}`;
  return request(path, options);
};

export const getLatestNewsList = ({ userId, limit = 15, offset = 0, minMatchRate = 0.75, language, lang, ...options } = {}) => {
  const query = new URLSearchParams();
  query.set("limit", String(limit));
  query.set("offset", String(offset));
  if (typeof minMatchRate === "number") query.set("min_match_rate", String(minMatchRate));
  const effectiveLang = normalizeLanguageCode(language || lang || getSelectedLanguage());
  if (effectiveLang) query.set("lang", effectiveLang);
  const qs = query.toString();
  const base = userId
    ? `/api/news/latest/matches/${encodeURIComponent(userId)}`
    : "/api/news/latest_list";
  return request(`${base}?${qs}`, options);
};

export const getMaterialInsights = ({ materialCode, limit = 20, offset = 0, ...options } = {}) => {
  if (!materialCode) {
    throw new Error("materialCode is required");
  }
  const query = new URLSearchParams();
  if (typeof limit === "number") query.set("limit", String(limit));
  if (typeof offset === "number") query.set("offset", String(offset));
  const qs = query.toString();
  return request(`/api/materials/${encodeURIComponent(materialCode)}/insights${qs ? `?${qs}` : ""}`, options);
};

export const getDisplayArticleLatestVersion = ({ id, userId, language, ...options }) => {
  const query = new URLSearchParams();
  if (typeof userId !== "undefined" && userId !== null && userId !== "") {
    query.set("user_id", String(userId));
  }
  if (typeof language !== "undefined" && language !== null && String(language).trim() !== "") {
    query.set("lang", normalizeLanguageCode(String(language)));
  }
  const qs = query.toString();
  const path = `/api/news/${encodeURIComponent(id)}/latest${qs ? `?${qs}` : ""}`;
  return request(path, { ...options, credentials: "include" });
};

export const getDisplayArticleConnections = ({ id, limit = 20, ...options }) => {
  if (typeof id === "undefined" || id === null) {
    throw new Error("display article id is required");
  }
  const query = new URLSearchParams();
  if (typeof limit === "number" && Number.isFinite(limit)) {
    query.set("limit", String(limit));
  }
  const qs = query.toString();
  const path = `/api/news/${encodeURIComponent(id)}/connections${qs ? `?${qs}` : ""}`;
  return request(path, { ...options, credentials: "include" });
};

export const getDisplayArticleAffectedPrices = ({ id, limit = 20, signal, ...options } = {}) => {
  if (typeof id === "undefined" || id === null) {
    throw new Error("display article id is required");
  }
  const query = new URLSearchParams();
  if (typeof limit === "number" && Number.isFinite(limit)) {
    query.set("limit", String(limit));
  }
  const qs = query.toString();
  const path = `/api/news/${encodeURIComponent(id)}/affected_prices${qs ? `?${qs}` : ""}`;
  const requestOptions = { ...options, credentials: "include" };
  if (signal) requestOptions.signal = signal;
  return request(path, requestOptions);
};

export const getDisplayArticleSimilar = ({ id, limit, signal, ...options } = {}) => {
  if (typeof id === "undefined" || id === null) {
    throw new Error("display article id is required");
  }
  const query = new URLSearchParams();
  if (typeof limit === "number" && Number.isFinite(limit)) {
    query.set("limit", String(limit));
  }
  const qs = query.toString();
  const path = `/api/news/${encodeURIComponent(id)}/similar${qs ? `?${qs}` : ""}`;
  const requestOptions = { ...options, credentials: "include" };
  if (signal) requestOptions.signal = signal;
  return request(path, requestOptions);
};

export const getDisplayArticleSimilarDetailed = ({ id, userId, limit, signal, ...options } = {}) => {
  if (typeof id === "undefined" || id === null) {
    throw new Error("display article id is required");
  }
  const query = new URLSearchParams();
  if (typeof limit === "number" && Number.isFinite(limit)) {
    query.set("limit", String(limit));
  }
  if (typeof userId !== "undefined" && userId !== null && userId !== "") {
    query.set("user_id", String(userId));
  }
  const qs = query.toString();
  const path = `/api/news/${encodeURIComponent(id)}/similar/detailed${qs ? `?${qs}` : ""}`;
  const requestOptions = { ...options, credentials: "include" };
  if (signal) requestOptions.signal = signal;
  return request(path, requestOptions);
};

export const rewriteDisplayArticle = ({ payload, userId, language, ...options }) => {
  const body = JSON.stringify({
    ...payload,
    user_id: typeof userId !== "undefined" ? userId : undefined,
    language: normalizeLanguageCode(language || payload?.language || getSelectedLanguage()),
  });
  return request("/api/news/rewrite", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body,
    credentials: "include",
    ...options,
  });
};

export const searchNews = ({
  query,
  limit = 20,
  includeBranches = true,
  ai = false,
  userId,
  language,
  signal,
} = {}) => {
  const url = new URL(apiUrl("/api/search/news"));
  url.searchParams.set("query", query ?? "");
  url.searchParams.set("limit", String(limit));
  if (includeBranches) url.searchParams.set("include_branches", "true");
  if (ai) url.searchParams.set("ai", "true");
  if (userId) url.searchParams.set("user_id", String(userId));
  url.searchParams.set("lang", normalizeLanguageCode(language || getSelectedLanguage()));

  return request(url.toString(), { signal, credentials: "include" });
};

export const getNewsBranches = ({ key, signal } = {}) =>
  request(`/api/search/news/branches?key=${encodeURIComponent(key)}`, {
    signal,
    credentials: "include",
  });
